export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:1337/api';
export const STRAPI_BASE_URL = API_URL.replace(/\/api\/?$/, '');

export const getMediaUrl = (url?: string | null): string => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
    return url;
  }
  const cleanUrl = url.startsWith('/') ? url : `/${url}`;
  return `${STRAPI_BASE_URL}${cleanUrl}`;
};

export const slugify = (text: string) => {
  const trMap: { [key: string]: string } = {
    'ç': 'c', 'ğ': 'g', 'ı': 'i', 'ö': 'o', 'ş': 's', 'ü': 'u',
    'Ç': 'C', 'Ğ': 'G', 'İ': 'I', 'Ö': 'O', 'Ş': 'S', 'Ü': 'U'
  };
  let result = text;
  for (let key in trMap) {
    result = result.replace(new RegExp(key, 'g'), trMap[key]);
  }

  return result
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')           // Replace spaces with -
    .replace(/[^\w\-]+/g, '')       // Remove all non-word chars
    .replace(/\-\-+/g, '-')         // Replace multiple - with single -
    .replace(/^-+/, '')             // Trim - from start of text
    .replace(/-+$/, '');            // Trim - from end of text
};

export const normalizeStrapiLocale = (raw?: string | null): string => {
  if (!raw) return 'tr';
  const clean = raw.toLowerCase().split(/[-_]/)[0];
  if (clean === 'ku' || clean === 'kmr' || clean === 'krm') return 'kmr';
  if (clean === 'en') return 'en';
  if (clean === 'ar') return 'ar';
  return 'tr';
};

const fetchLocalizedContent = async (endpoint: string, rawLocale: string = 'tr', customQueryString: string = '', populateParam: string = 'populate=*', page: number = 1, pageSize: number = 100) => {
  const locale = normalizeStrapiLocale(rawLocale);
  
  let sortParam = 'sort[0]=createdAt:desc';
  if (endpoint === 'articles' || endpoint === 'campaigns') {
    sortParam = 'sort[0]=publishedDate:desc&sort[1]=createdAt:desc';
  } else if (endpoint === 'reports') {
    sortParam = 'sort[0]=reportDate:desc&sort[1]=createdAt:desc';
  }

  let urlTarget = `${API_URL}/${endpoint}?${populateParam}&locale=${locale}&filters[locale][$eq]=${locale}&${sortParam}&pagination[page]=${page}&pagination[pageSize]=${pageSize}${customQueryString}`;

  const resTarget = await fetch(urlTarget);

  if (!resTarget.ok) {
    throw new Error('Network response was not ok');
  }

  let dataTarget = await resTarget.json();
  let data = dataTarget.data || [];
  
  // Strict locale enforcement: No fallback
  data = data.filter((item: any) => {
    const itemLocale = item.attributes?.locale || item.locale;
    if (!itemLocale) return true; // If Strapi didn't return locale, don't break it
    return itemLocale === locale;
  });
  
  // Frontend Fallback for missing dates
  data.forEach((item: any) => {
    if ((endpoint === 'articles' || endpoint === 'campaigns') && !item.publishedDate) {
      item.publishedDate = item.publishedAt || item.createdAt || new Date().toISOString();
    }
    if (endpoint === 'reports' && !item.reportDate) {
      item.reportDate = item.publishedAt || item.createdAt || new Date().toISOString();
    }
  });

  return { data, meta: dataTarget.meta };
};

export const getArticles = async (query?: string, rawLocale: string = 'tr', page: number = 1, pageSize: number = 100, category?: string) => {
  try {
    const locale = normalizeStrapiLocale(rawLocale);
    let customQuery = '';
    if (query) {
      customQuery += `&filters[$or][0][title][$contains]=${encodeURIComponent(query)}&filters[$or][1][content][$contains]=${encodeURIComponent(query)}`;
    }
    if (category && category !== 'all') {
      customQuery += `&filters[category][$eq]=${encodeURIComponent(category)}`;
    }
    const populateParam = 'populate=coverImage,documentFile,writer,writer.avatar,localizations';
    return await fetchLocalizedContent('articles', locale, customQuery, populateParam, page, pageSize);
  } catch (error) {
    console.error('Error fetching articles:', error);
    return { data: [], meta: null };
  }
};

export const getCampaigns = async (query?: string, rawLocale: string = 'tr', page: number = 1, pageSize: number = 100) => {
  try {
    const locale = normalizeStrapiLocale(rawLocale);
    let customQuery = '';
    if (query) {
      customQuery += `&filters[$or][0][title][$contains]=${encodeURIComponent(query)}&filters[$or][1][content][$contains]=${encodeURIComponent(query)}`;
    }
    const populateParam = 'populate=coverImage,localizations';
    return await fetchLocalizedContent('campaigns', locale, customQuery, populateParam, page, pageSize);
  } catch (error) {
    console.error('Error fetching campaigns:', error);
    return { data: [], meta: null };
  }
};

export const getReports = async (query?: string, locale: string = 'tr', page: number = 1, pageSize: number = 100) => {
  try {
    let customQuery = '';
    if (query) {
      customQuery += `&filters[$or][0][title][$contains]=${encodeURIComponent(query)}&filters[$or][1][description][$contains]=${encodeURIComponent(query)}`;
    }
    return await fetchLocalizedContent('reports', locale, customQuery, 'populate=*', page, pageSize);
  } catch (error) {
    console.error('Error fetching reports:', error);
    return { data: [], meta: null };
  }
};

export const getLeadership = async (locale: string = 'tr') => {
  locale = locale === 'krm' ? 'kmr' : locale;
  try {
    // Leadership doesn't need search query usually, just fetch all and sort by order
    const urlTarget = `${API_URL}/leaderships?populate=*&locale=${locale}&sort=order:asc`;
    const resTarget = await fetch(urlTarget);
    if (!resTarget.ok) throw new Error('Network response was not ok');
    let dataTarget = await resTarget.json();
    let data = dataTarget.data || [];
    
    data = data.filter((item: any) => {
      const itemLocale = item.attributes?.locale || item.locale;
      if (!itemLocale) return true;
      return itemLocale === locale;
    });
    
    // No fallback
    
    return data;
  } catch (error) {
    console.error('Error fetching leadership:', error);
    return [];
  }
};

export const getWriters = async (locale: string = 'tr') => {
  locale = locale === 'krm' ? 'kmr' : locale;
  try {
    const urlTarget = `${API_URL}/writers?populate=*&locale=${locale}&sort=name:asc`;
    const resTarget = await fetch(urlTarget);
    if (!resTarget.ok) throw new Error('Network response was not ok');
    let dataTarget = await resTarget.json();
    let data = dataTarget.data || [];
    
    data = data.filter((item: any) => {
      const itemLocale = item.attributes?.locale || item.locale;
      if (!itemLocale) return true;
      return itemLocale === locale;
    });
    
    // No fallback
    
    return data;
  } catch (error) {
    console.error('Error fetching writers:', error);
    return [];
  }
};

export const getArticleBySlug = async (identifier: string, locale: string = 'tr') => {
  locale = locale === 'krm' ? 'kmr' : locale;
  try {
    const isNumeric = /^\d+$/.test(identifier);
    const idFilter = isNumeric ? `&filters[$or][2][id][$eq]=${identifier}` : '';
    const encodedIdentifier = encodeURIComponent(identifier);

    const urlTarget = `${API_URL}/articles?filters[$or][0][slug][$eq]=${encodedIdentifier}&filters[$or][1][documentId][$eq]=${encodedIdentifier}${idFilter}&populate=coverImage,documentFile,writer,writer.avatar,localizations&locale=${locale}`;
    const resTarget = await fetch(urlTarget);

    if (resTarget.ok) {
      let dataTarget = await resTarget.json();
      let data = dataTarget.data || [];

      data = data.filter((item: any) => {
        const itemLocale = item.attributes?.locale || item.locale;
        if (!itemLocale) return true;
        return itemLocale === locale;
      });

      if (data && data.length > 0) {
        const article = data[0];
        if (!article.publishedDate) {
          article.publishedDate = article.publishedAt || article.createdAt;
        }
        return article;
      }
    }

    // Fallback 1: Query by documentId without strict locale (e.g. if accessed across languages)
    const docRes = await fetch(`${API_URL}/articles?filters[documentId][$eq]=${encodedIdentifier}&populate=coverImage,documentFile,writer,writer.avatar,localizations`);
    if (docRes.ok) {
      const docData = await docRes.json();
      const articles = docData.data || [];
      if (articles.length > 0) {
        const matched = articles.find((a: any) => (a.attributes?.locale || a.locale) === locale) || articles[0];
        if (!matched.publishedDate) {
          matched.publishedDate = matched.publishedAt || matched.createdAt;
        }
        return matched;
      }
    }

    // Fallback 2: Fetch newest articles (sorted newest first by id:desc) and match by slugify(title) or id
    const fallbackUrl = `${API_URL}/articles?populate=coverImage,documentFile,writer,writer.avatar,localizations&locale=${locale}&sort[0]=id:desc&pagination[pageSize]=100`;
    const fallbackRes = await fetch(fallbackUrl);
    if (fallbackRes.ok) {
      let allDataTarget = await fallbackRes.json();
      let allArticles = allDataTarget.data || [];
      allArticles = allArticles.filter((item: any) => {
        const itemLocale = item.attributes?.locale || item.locale;
        if (!itemLocale) return true;
        return itemLocale === locale;
      });

      const matchedArticle = allArticles.find((item: any) => 
        (item.slug && item.slug === identifier) ||
        slugify(item.title) === identifier ||
        String(item.id) === identifier ||
        item.documentId === identifier
      );
      if (matchedArticle) {
        if (!matchedArticle.publishedDate) {
          matchedArticle.publishedDate = matchedArticle.publishedAt || matchedArticle.createdAt;
        }
        return matchedArticle;
      }
    }

    // Fallback 3: Query campaigns endpoint (if item was added as a Campaign)
    const campUrl = `${API_URL}/campaigns?filters[$or][0][slug][$eq]=${encodedIdentifier}&filters[$or][1][documentId][$eq]=${encodedIdentifier}${idFilter}&populate=coverImage,localizations&locale=${locale}`;
    const campRes = await fetch(campUrl);
    if (campRes.ok) {
      const campJson = await campRes.json();
      let campData = campJson.data || [];
      campData = campData.filter((item: any) => {
        const itemLocale = item.attributes?.locale || item.locale;
        if (!itemLocale) return true;
        return itemLocale === locale;
      });
      if (campData && campData.length > 0) {
        const camp = campData[0];
        if (!camp.publishedDate) {
          camp.publishedDate = camp.publishedAt || camp.createdAt;
        }
        camp.category = 'Kampanya';
        return camp;
      }
    }

    // Fallback 4: Query newest campaigns and match by slug/slugify/id/documentId
    const campListUrl = `${API_URL}/campaigns?populate=coverImage,localizations&locale=${locale}&sort[0]=id:desc&pagination[pageSize]=100`;
    const campListRes = await fetch(campListUrl);
    if (campListRes.ok) {
      const campListJson = await campListRes.json();
      let allCamps = campListJson.data || [];
      allCamps = allCamps.filter((item: any) => {
        const itemLocale = item.attributes?.locale || item.locale;
        if (!itemLocale) return true;
        return itemLocale === locale;
      });
      const matchedCamp = allCamps.find((item: any) =>
        (item.slug && item.slug === identifier) ||
        slugify(item.title) === identifier ||
        String(item.id) === identifier ||
        item.documentId === identifier
      );
      if (matchedCamp) {
        if (!matchedCamp.publishedDate) {
          matchedCamp.publishedDate = matchedCamp.publishedAt || matchedCamp.createdAt;
        }
        matchedCamp.category = 'Kampanya';
        return matchedCamp;
      }
    }

    return null;
  } catch (error) {
    console.error('Error fetching article by slug:', error);
    return null;
  }
};

export const getPrograms = async (locale: string = 'tr', page: number = 1, pageSize: number = 25) => {
  try {
    return await fetchLocalizedContent('programs', locale, undefined, 'populate=*', page, pageSize);
  } catch (error) {
    console.error('Error fetching programs:', error);
    return { data: [], meta: null };
  }
};

export const getProgramById = async (identifier: string, locale: string = 'tr') => {
  locale = locale === 'krm' ? 'kmr' : locale;
  try {
    const urlTarget = `${API_URL}/programs?filters[documentId][$eq]=${identifier}&populate=*&locale=${locale}`;
    const resTarget = await fetch(urlTarget);
    if (!resTarget.ok) throw new Error('Network response was not ok');
    let dataTarget = await resTarget.json();
    let data = dataTarget.data || [];
    
    data = data.filter((item: any) => {
      const itemLocale = item.attributes?.locale || item.locale;
      if (!itemLocale) return true;
      return itemLocale === locale;
    });
    
    // No fallback

    if (data && data.length > 0) {
      return data[0];
    }
    return null;
  } catch (error) {
    console.error('Error fetching program by id:', error);
    return null;
  }
};

export const searchContent = async (query: string, locale: string = 'tr') => {
  const [articlesRes, reportsRes] = await Promise.all([
    getArticles(query, locale),
    getReports(query, locale)
  ]);

  return [
    ...articlesRes.data.map((a: any) => ({ ...a, type: 'article' })),
    ...reportsRes.data.map((r: any) => ({ ...r, type: 'report' }))
  ];
};

export const getGalleries = async (locale: string = 'tr', categoryName?: string, page: number = 1, pageSize: number = 12) => {
  try {
    let customQuery = '';
    if (categoryName && categoryName !== 'all') {
      customQuery += `&filters[category][$eq]=${encodeURIComponent(categoryName)}`;
    }
    const populateParam = 'populate=*';
    return await fetchLocalizedContent('galleries', locale, customQuery, populateParam, page, pageSize);
  } catch (error) {
    console.error('Error fetching galleries:', error);
    return { data: [], meta: null };
  }
};

export const sendMessage = async (messageData: { name: string; email: string; subject: string; body: string }) => {
  try {
    const response = await fetch(`${API_URL}/messages`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ data: messageData }),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error?.message || 'Mesaj gönderilemedi.');
    }
    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

export interface SocialMediaData {
  instagram?: string;
  x?: string;
  tiktok?: string;
  youtube?: string;
  facebook?: string;
}

export const getSocialMedia = async (): Promise<SocialMediaData | null> => {
  try {
    const response = await fetch(`${API_URL}/social-media`);
    if (!response.ok) return null;
    const json = await response.json();
    if (json && json.data) {
      return {
        instagram: json.data.instagram || '',
        x: json.data.x || '',
        tiktok: json.data.tiktok || '',
        youtube: json.data.youtube || '',
        facebook: json.data.facebook || '',
      };
    }
    return null;
  } catch (error) {
    console.error('Error fetching social media from Strapi:', error);
    return null;
  }
};

